import { Link, createFileRoute } from '@tanstack/react-router';
import { LegalPageFrame } from '~/components/sections/LegalDocument';
import { LegalPagesUnavailable } from '~/components/sections/LegalPagesUnavailable';
import { termsContent } from '~/content/terms';
import { getLegalPageSummaries, LegalPagesUnavailableError } from '~/lib/payload';
import { pageHeadMeta } from '~/lib/seo';
import { termsIndexGroups } from '~/lib/terms-index';
import readingStylesHref from '~/styles/reading.css?url';

/**
 * Terms & Conditions index. The listed documents come from Payload (legal pages
 * with "Show on Terms & Conditions page" ticked), so this route is rendered at
 * request time rather than prerendered. `/terms-conditions` and the Webflow-era
 * `/terms/terms-conditions` redirect here.
 */
export const Route = createFileRoute('/terms/')({
  loader: async () => {
    const pages = await getLegalPageSummaries();
    if (pages === null) throw new LegalPagesUnavailableError();
    return { groups: termsIndexGroups(pages) };
  },
  errorComponent: LegalPagesUnavailable,
  head: () =>
    pageHeadMeta({
      title: 'Terms & Conditions',
      description:
        'The terms and conditions that apply to the use of the nsave website, mobile app, products and services.',
      path: '/terms',
      stylesheets: [readingStylesHref],
    }),
  component: TermsConditionsPage,
});

function TermsDocLink({ href, label }: { href: string; label: string }) {
  const className =
    'link-underline-lr inline-block bg-origin-content py-3 text-body text-grey-900 hover:text-black';

  if (href === '/terms/cookie') {
    return (
      <Link to="/terms/cookie" className={className}>
        {label}
      </Link>
    );
  }

  const splat = href.replace(/^\/terms\//, '');
  return (
    <Link to="/terms/$" params={{ _splat: splat }} className={className}>
      {label}
    </Link>
  );
}

function TermsConditionsPage() {
  const { groups } = Route.useLoaderData();
  return (
    <LegalPageFrame>
      <h1 className="text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
        {termsContent.title}
      </h1>
      <p className="mt-6 text-body-m text-grey-500">Last updated: {termsContent.lastUpdated}</p>

      <div className="mt-10 max-w-[640px] space-y-6">
        {termsContent.intro.map((paragraph) => (
          <p key={paragraph} className="text-body text-grey-900">
            {paragraph}
          </p>
        ))}
      </div>

      <div className="mt-[96px] space-y-[84px]">
        {groups.map((group) => (
          <section key={group.title}>
            <h2 className="text-h5 font-semibold tracking-[-0.02em] md:text-h4">{group.title}</h2>
            <ul className="mt-10 flex flex-col">
              {group.links.map((link) => (
                <li key={link.label} className="flex">
                  <TermsDocLink href={link.href} label={link.label} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </LegalPageFrame>
  );
}
